<template>
  <div class="case-items-panel">
    <Molded>
      <div class="case-items-panel-toolbar">
        <div class="case-items-panel-barcode">
          <InputText title="Digite o Nº Código e pressione enter ou utilize o Leitor Óptico"
            classIconTitle="fal fa-barcode-read" field="barCode" :formName="formName" :required="false"
            :maxLength="500" :markFormDirty="false" :enter="addByBarcode" v-model="barCode" />
        </div>
        <Button _key="btnCaseSelectPatrimony" type="info" title="Selecionar Patrimônios"
          classIcon="fa-solid fa-magnifying-glass" size="small" :clicked="openSelectPatrimony" />
      </div>
    </Molded>

    <div class="case-items-panel-separator"></div>

    <Molded :paddingLeft="0" :paddingRight="0">
      <div class="case-items-panel-tools">
        <input v-model="searchTerm" type="text" class="case-items-panel-search"
          placeholder="Pesquisar patrimônio, produto ou série..." />
        <div class="case-items-panel-total text-center">
          Total <b>{{ filteredList.length }}</b> registros
        </div>
      </div>

      <div class="case-items-panel-header">
        <div class="case-items-panel-col-drag"></div>
        <div class="case-items-panel-col-pat">Nº Patrimônio</div>
        <div class="case-items-panel-col-product">Produto</div>
        <div class="case-items-panel-col-serial">Nº Série</div>
        <div class="case-items-panel-col-status text-center">Status</div>
        <div class="case-items-panel-col-action"></div>
      </div>

      <Loading type="line" :center="false" v-if="loading" />

      <draggable v-else v-model="items" tag="div" :animation="200" ghost-class="case-items-panel-ghost"
        :disabled="isDragDisabled" handle=".case-items-panel-drag-handle" @end="onReorder">
        <div class="case-items-panel-row" v-for="item in filteredList" :key="item.id">
          <div class="case-items-panel-col-drag">
            <i class="fa-solid fa-grip-lines case-items-panel-drag-handle"></i>
          </div>

          <div class="case-items-panel-col-pat">
            <span class="case-items-panel-pat-pill">{{ item.barCode }}</span>
          </div>

          <div class="case-items-panel-col-product">
            <span class="case-items-panel-product" :title="item.productFullName">
              {{ item.productFullName }}
            </span>
          </div>

          <div class="case-items-panel-col-serial">
            <span v-if="item.serialNumber" class="case-items-panel-serial-pill">{{ item.serialNumber }}</span>
          </div>

          <div class="case-items-panel-col-status text-center">
            <span class="case-items-panel-status-pill" :class="statusClass(item.statusName)">
              {{ item.statusName }}
            </span>
          </div>

          <div class="case-items-panel-col-action">
            <i class="fa-solid fa-xmark case-items-panel-remove" title="Remover da case"
              @click="remove(item.id)"></i>
          </div>
        </div>
      </draggable>

      <div class="text-center case-items-panel-empty" v-if="!loading && filteredList.length === 0">
        <br />
        Nenhum registro encontrado!
      </div>
    </Molded>

    <Modal title="Selecionar Patrimônios" :width="600" v-if="showModal('caseSelectPatrimony')">
      <CheckboxServer title="patrimônio(s)" :showSearch="true" url="/api/v1/stock/patrimony/select-all-with-product"
        v-model="selectedPatrimonyIds" />
      <div class="text-right">
        <Button _key="btnAddSelectedPatrimony" type="success" title="Adicionar" classIcon="fa-solid fa-plus"
          size="medium" :disabled="selectedPatrimonyIds.length === 0" :clicked="addSelectedPatrimonies" />
      </div>
    </Modal>
  </div>
</template>

<script>
import Button from "@/ui/forms/Button";
import InputText from "@/ui/forms/InputText";
import Modal from "@/ui/forms/Modal";
import Molded from "@/ui/layout/Molded";
import CheckboxServer from "@/ui/forms/CheckboxServer";
import Loading from "@/ui/shared/Loading.vue";

import draggable from "vuedraggable";

import ItemsCaseService from "../../services/items-case.service";

import { mapGetters, mapMutations } from "vuex";

export default {
  name: "CaseItemsPanel",
  components: { Button, InputText, Modal, Molded, CheckboxServer, Loading, draggable },
  props: {
    caseId: { type: String, required: true },
  },
  data() {
    return {
      formName: "caseItemsPanel",
      loading: true,
      items: [],
      barCode: "",
      searchTerm: "",
      selectedPatrimonyIds: [],
    };
  },
  mounted() {
    this.getAll();
  },
  computed: {
    ...mapGetters("generic", ["showModal"]),
    isDragDisabled() {
      return this.searchTerm !== "";
    },
    filteredList() {
      if (!this.searchTerm) return this.items;
      const term = this.searchTerm.toLowerCase();
      return this.items.filter((item) => {
        return (
          (item.barCode || "").toLowerCase().includes(term) ||
          (item.productFullName || "").toLowerCase().includes(term) ||
          (item.serialNumber || "").toLowerCase().includes(term)
        );
      });
    },
  },
  methods: {
    ...mapMutations("generic", ["openModal", "hideModal", "removeLoading"]),
    async getAll() {
      this.loading = true;
      try {
        const response = await ItemsCaseService.getAll(this.caseId);
        this.items = response.content.data;
        this.$emit("changed", this.items.length);
      } finally {
        this.loading = false;
      }
    },
    async addByBarcode() {
      if (!this.barCode) return;
      const barCode = this.barCode;
      this.barCode = "";
      try {
        await ItemsCaseService.createByBarcode(this.caseId, barCode);
        await this.getAll();
      } catch (error) {
        this.barCode = "";
      }
    },
    openSelectPatrimony() {
      this.selectedPatrimonyIds = [];
      this.openModal("caseSelectPatrimony");
      this.removeLoading(["btnCaseSelectPatrimony"]);
    },
    async addSelectedPatrimonies() {
      let order = this.items.length;
      try {
        for (const patrimonyId of this.selectedPatrimonyIds) {
          await ItemsCaseService.create(this.caseId, patrimonyId, order++);
        }
        this.selectedPatrimonyIds = [];
        this.hideModal();
        await this.getAll();
      } finally {
        this.removeLoading(["btnAddSelectedPatrimony"]);
      }
    },
    async onReorder() {
      await ItemsCaseService.orderAll(this.items.map((item) => item.id));
    },
    async remove(itemId) {
      await ItemsCaseService.remove([itemId]);
      this.items = this.items.filter((item) => item.id !== itemId);
      this.$emit("changed", this.items.length);
    },
    statusClass(statusName) {
      switch (statusName) {
        case "Disponível":
          return "case-items-panel-status-disponivel";
        case "Locado":
          return "case-items-panel-status-locado";
        case "Inativo":
          return "case-items-panel-status-inativo";
        case "Manutenção":
          return "case-items-panel-status-manutencao";
        default:
          return "";
      }
    },
  },
};
</script>

<style scoped>
.case-items-panel-separator {
  height: 16px;
}

.case-items-panel-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
}

.case-items-panel-barcode {
  flex: 1;
  max-width: 480px;
}

.case-items-panel-tools {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 18px;
  margin-bottom: 15px;
}

.case-items-panel-search {
  flex: 1;
  padding: 5px 10px;
  font-size: 13px;
  border-radius: 50px;
  border: 1px solid var(--border-input);
  background: var(--input-bg);
  color: var(--input-color);
  outline: none;
}

.case-items-panel-total {
  background: var(--bg-elevated);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  width: 150px;
  flex-shrink: 0;
}

.case-items-panel-header,
.case-items-panel-row {
  display: flex;
  align-items: center;
  padding: 10px 18px;
}

.case-items-panel-header {
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--border-subtle);
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
}

.case-items-panel-row {
  border-bottom: 1px solid var(--border-subtle);
}

.case-items-panel-col-drag {
  flex: 0 0 26px;
}

.case-items-panel-col-pat {
  flex: 0 0 400px;
}

.case-items-panel-col-product {
  flex: 1 1 auto;
  min-width: 0;
  padding-right: 12px;
}

.case-items-panel-col-serial {
  flex: 0 0 170px;
}

.case-items-panel-col-status {
  flex: 0 0 130px;
}

.case-items-panel-col-action {
  flex: 0 0 40px;
  display: flex;
  justify-content: flex-end;
}

.case-items-panel-drag-handle {
  font-size: 13px;
  color: var(--text-muted);
  cursor: grab;
}

.case-items-panel-drag-handle:hover {
  color: var(--text-secondary);
}

.case-items-panel-pat-pill {
  display: inline-block;
  background: var(--pill-patrimony-bg);
  color: var(--pill-patrimony-text);
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 13px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.case-items-panel-product {
  font-size: 13px;
  color: var(--text-primary);
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-items-panel-serial-pill {
  display: inline-block;
  background: var(--bg-elevated);
  color: var(--text-secondary);
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 13px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.case-items-panel-status-pill {
  display: inline-block;
  padding: 3px 14px;
  border-radius: 14px;
  font-size: 12px;
  color: var(--text-inverse);
  text-transform: capitalize;
}

.case-items-panel-status-disponivel {
  background: var(--color-success);
}

.case-items-panel-status-locado {
  background: var(--pill-status-rented);
}

.case-items-panel-status-inativo {
  background: var(--color-danger);
}

.case-items-panel-status-manutencao {
  background: var(--color-warning);
}

.case-items-panel-remove {
  font-size: 14px;
  color: var(--color-danger);
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.15s;
}

.case-items-panel-remove:hover {
  opacity: 1;
}

.case-items-panel-empty {
  color: var(--text-muted);
  font-size: 13px;
  padding-bottom: 10px;
}

::v-deep .case-items-panel-ghost {
  opacity: 0.4;
  background: var(--bg-elevated);
}
</style>
